/**
 * Preprocess camera frames to improve dense / printed QR detection.
 */
export const drawCenterCrop = (
  video,
  canvas,
  ctx,
  cropRatio,
  maxDimension = null,
  maxUpscale = 4,
) => {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return false;

  const cropW = vw * cropRatio;
  const cropH = vh * cropRatio;
  const sx = (vw - cropW) / 2;
  const sy = (vh - cropH) / 2;

  let targetW = cropW;
  let targetH = cropH;

  if (maxDimension) {
    const scale = Math.min(
      maxDimension / cropW,
      maxDimension / cropH,
      maxUpscale,
    );
    targetW = Math.max(1, Math.floor(cropW * scale));
    targetH = Math.max(1, Math.floor(cropH * scale));
  } else {
    targetW = Math.max(1, Math.floor(cropW));
    targetH = Math.max(1, Math.floor(cropH));
  }

  canvas.width = targetW;
  canvas.height = targetH;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(video, sx, sy, cropW, cropH, 0, 0, targetW, targetH);
  return true;
};

/**
 * `contrast` is on the standard -255..255 scale: 0 leaves the image untouched.
 * It is clamped because the factor formula divides by (259 - contrast): past 259 it
 * goes negative, which inverts the image and collapses it to a hard threshold at
 * mid-gray.
 */
export const enhanceFrameForQr = (
  ctx,
  width,
  height,
  { invert = false, contrast = 60 } = {},
) => {
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const c = Math.max(-255, Math.min(255, contrast));
  const factor = (259 * (c + 255)) / (255 * (259 - c));

  for (let i = 0; i < data.length; i += 4) {
    let r = data[i];
    let g = data[i + 1];
    let b = data[i + 2];

    r = factor * (r - 128) + 128;
    g = factor * (g - 128) + 128;
    b = factor * (b - 128) + 128;

    const gray = 0.299 * r + 0.587 * g + 0.114 * b;
    const value = invert ? 255 - gray : gray;
    const clamped = Math.min(255, Math.max(0, value));

    data[i] = clamped;
    data[i + 1] = clamped;
    data[i + 2] = clamped;
  }

  ctx.putImageData(imageData, 0, 0);
};

export const sharpenFrameForQr = (ctx, width, height, amount = 0.65) => {
  const imageData = ctx.getImageData(0, 0, width, height);
  const src = imageData.data;
  const out = new Uint8ClampedArray(src);
  const w = width;

  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const idx = (y * w + x) * 4;
      const center = src[idx];
      const blur =
        (src[idx - 4] + src[idx + 4] + src[idx - w * 4] + src[idx + w * 4]) / 4;
      const sharp = Math.min(
        255,
        Math.max(0, center + (center - blur) * amount),
      );
      out[idx] = sharp;
      out[idx + 1] = sharp;
      out[idx + 2] = sharp;
    }
  }

  imageData.data.set(out);
  ctx.putImageData(imageData, 0, 0);
};
